/******************************************************************************
 *                                                                            *
 *  Copyright (c) 2018, 2025 by AsterionDB Inc.                               *
 *                                                                            *
 *  All rights reserved.  No part of this work may be reproduced or otherwise *
 *  incorporated into other works without the express written consent of      *
 *  AsterionDB Inc.                                                           *
 *                                                                            *
 *****************************************************************************/

import React from 'react'

import { Row, Input, Button, FormGroup, Label, Col } from 'reactstrap'
import Select from 'react-select'
import InputNumber from 'rc-input-number';

import {AppContext} from '../../AppContext';
import { config } from 'react-transition-group';

class RagServerConfig extends React.Component
{
  indexDistanceMetrics = 
  [
    {label: "COSINE", value: "COSINE"},
    {label: "MANHATTAN", value: 'MANHATTAN'},
    {label: "HAMMING", value: 'HAMMING'},
    {label: "JACCARD", value: 'JACCARD'},
    {label: "DOT", value: 'DOT'},
    {label: "EUCLIDEAN", value: 'EUCLIDEAN'},
    {label: "L2_SQUARED", value: 'L2_SQUARED'},
    {label: "EUCLIDEAN_SQUARED", value: 'EUCLIDEAN_SQUARED'}
  ];

  indexTypes = 
  [
    {label: 'Hierarchical Navigable Small World', value: 'HNSW'},
    {label: 'Inverted Flat File', value: 'IVF'}
  ];

  splitModeValues = 
  [
    {label: 'By Words', value: 'WORDS'},
    {label: 'By Characters', value: 'CHARACTERS'}
  ];

  splitByValues =
  [
    {label: 'Max Chunk Size', value: 'none'},
    {label: 'Newline', value: 'newline'},
    {label: 'Blank Line', value: 'blankline'},
    {label: 'Space', value: 'space'},
    {label: 'Recursively', value: 'recursively'},
    {label: 'Sentence', value: 'sentence'}
  ];

  componentDidMount()
  {
    this.getConfiguration();
    this.getStoredCredentials();
    this.getAiProviders();
  }

  constructor(props)
  {
    super();

    this.state =
    {
      configuration: undefined,
      storedCredentials: [],
      aiProviders: [],
      additionalParameters: undefined,
      saveButtonDisabled: true
    }

    this.setIndexType = this.setIndexType.bind(this);
    this.setIndexDistanceMetric = this.setIndexDistanceMetric.bind(this);
    this.setAiProvider = this.setAiProvider.bind(this);
    this.setAdditionalParameters = this.setAdditionalParameters.bind(this);
    this.saveChanges = this.saveChanges.bind(this);
    this.setCredentialName = this.setCredentialName.bind(this);
  }

  async getAiProviders()
  {
    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getAiProviders');
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error getting AI providers.');

    this.setState(
    {
      aiProviders: result.jsonData.aiProviders
    });
  }

  async getConfiguration()
  {
    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getRagServerConfig');
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error getting RAG Server configuration');

    this.setState(
    {
      configuration: result.jsonData.configuration,
      additionalParameters: JSON.stringify(result.jsonData.configuration.llmProviderParameters.additionalParameters)
    });
  }

  async getStoredCredentials()
  {
    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'getStoredCredentials');
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Error getting stored credentials.');

    this.setState(
    {
      storedCredentials: result.jsonData.storedCredentials
    });
  }

  render()
  {    
    if (undefined === this.state.configuration) return null;

    return(
      <div>
        <Row style={{paddingTop: "10px"}}><Col><b>Default Index Parameters</b></Col></Row>
        <FormGroup style={{paddingTop: "10px"}} row>
          <Label sm='1' for='indexType'>Index Type</Label>
          <Col sm='2'>
            <Select id="indexType" value={this.indexTypes.find((element) => element.value === this.state.configuration.indexParameters.indexType.toUpperCase())} 
              options={this.indexTypes} onChange={this.setIndexType} isClearable={false} getOptionLabel={option => option.label}
              getOptionValue={option => option.value}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='indexDistanceMetrics'>Distance Function</Label>
          <Col sm='2'>
            <Select id="indexDistanceMetrics" value={this.indexDistanceMetrics.find((element) => element.value === this.state.configuration.indexParameters.distance.toUpperCase())} 
              options={this.indexDistanceMetrics} onChange={this.setIndexDistanceMetric} isClearable={false} getOptionLabel={option => option.label}
              getOptionValue={option => option.value}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='indexAccuracy'>Index Accuracy</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='indexAccuracy' min={33} max={100}
              onChange={this.setIndexParameterValue.bind(this, 'accuracy')} value={this.state.configuration.indexParameters.accuracy}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='neighbors'>Neighbors</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='neighbors' min={1} max={2048}
              onChange={this.setIndexParameterValue.bind(this, 'neighbors')} value={this.state.configuration.indexParameters.neighbors}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='efConstruction'>EF Construction</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='efConstruction' min={1} max={65535}
              onChange={this.setIndexParameterValue.bind(this, 'efConstruction')} value={this.state.configuration.indexParameters.efConstruction}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='samples'>Samples/Partition</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='samples' min={1} max={65535}
              onChange={this.setIndexParameterValue.bind(this, 'samples')} value={this.state.configuration.indexParameters.samples}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='minVectors'>Minimum Vectors</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='minVectors' min={0} max={65535}
              onChange={this.setIndexParameterValue.bind(this, 'minVectors')} value={this.state.configuration.indexParameters.minVectors}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='parallel'>Parallel Threads</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='parallel' min={0} max={65535}
              onChange={this.setIndexParameterValue.bind(this, 'parallel')} value={this.state.configuration.indexParameters.parallel}/>
          </Col>
        </FormGroup>
        <Row style={{paddingTop: "10px"}}><Col><b>Chunking Parameters</b></Col></Row>
        <FormGroup style={{paddingTop: "10px"}} row>
          <Label sm='1' for='maxJobs'>Concurrent Chunking Jobs</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='maxJobs' min={1} max={64}
              onChange={this.setConfigurationValue.bind(this, 'maxEmbeddingJobs')} value={this.state.configuration.maxEmbeddingJobs}/>
          </Col>
        </FormGroup>
        <FormGroup style={{paddingTop: "10px"}} row>
          <Label sm='1' for='splitMode'>Chunk Splitting Mode</Label>
          <Col sm='1'>
            <Select id="splitMode" value={this.splitModeValues.find((element) => element.value === this.state.configuration.chunkingParameters.by.toUpperCase())} 
              options={this.splitModeValues} onChange={this.setChunkingParameter.bind(this, 'by')} isClearable={false} getOptionLabel={option => option.label}
              getOptionValue={option => option.value}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='max'>Max Chunk Size</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='max' min={0} max={4000}
              onChange={this.setNumericChunkingParameter.bind(this, 'max')} value={this.state.configuration.chunkingParameters.max}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='splitBy'>Split Chunk Boundary</Label>
          <Col sm='1'>
            <Select id="splitBy" value={this.splitByValues.find((element) => element.value === this.state.configuration.chunkingParameters.split)} 
              options={this.splitByValues} onChange={this.setChunkingParameter.bind(this, 'split')} isClearable={false} getOptionLabel={option => option.label}
              getOptionValue={option => option.value}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='overlap'>Chunk Overlap</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='overlap' min={5} max={20}
              onChange={this.setNumericChunkingParameter.bind(this, 'overlap')} value={this.state.configuration.chunkingParameters.overlap}/>
          </Col>
        </FormGroup>
        <Row style={{paddingTop: "10px"}}><Col><b>Normalization Settings</b></Col></Row>
        <FormGroup style={{paddingTop: "10px"}} row>
          <Col sm='1' >
            <Label for='punctuation'>Punctuation</Label>
          </Col>
          <Col sm='2'>
            <Input type="checkbox" id="punctuation" name="punctuation" onChange={this.toggleNormOption.bind(this, 'punctuation')}
            checked={(this.state.configuration.chunkingParameters.norm_options.find((option => option === 'punctuation')) === 'punctuation' ? 'checked' : '')} style={{marginLeft: 0, marginTop: '0.7rem'}}
            title="Check this box to normalize based on the chunk's punctuation"/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm='1' >
            <Label for='whitespace'>Whitespace</Label>
          </Col>
          <Col sm='2'>
            <Input type="checkbox" id="whitespace" name="whitepace" onChange={this.toggleNormOption.bind(this, 'whitespace')}
            checked={(this.state.configuration.chunkingParameters.norm_options.find((option => option === 'whitespace')) === 'whitespace' ? 'checked' : '')} style={{marginLeft: 0, marginTop: '0.7rem'}}
            title="Check this box to use the vector index (if any) for this search"/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Col sm='1' >
            <Label for='widechars'>Wide Characters</Label>
          </Col>
          <Col sm='2'>
            <Input type="checkbox" id="widechar" name="widechar" onChange={this.toggleNormOption.bind(this, 'widechar')}
            checked={(this.state.configuration.chunkingParameters.norm_options.find((option => option === 'widechar')) === 'widechar' ? 'checked' : '')} style={{marginLeft: 0, marginTop: '0.7rem'}}
            title="Check this box to use the vector index (if any) for this search"/>
          </Col>
        </FormGroup>
        <Row style={{paddingTop: "10px", paddingBottom: "10px"}}><Col><b>LLM Settings</b></Col></Row>
        <FormGroup row>
          <Label sm='1' for='topK'>Results Returned</Label>
          <Col sm='2'>
            <InputNumber  style={{width: 100}} id='topK' min={1} max={64}
              onChange={this.setConfigurationValue.bind(this, 'topK')} value={this.state.configuration.topK}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='provider'>AI Provider</Label>
          <Col sm='2'>
            <Select id="provider" value={this.state.aiProviders.find((element) => element.providerName === this.state.configuration.llmProviderParameters.provider)} 
              options={this.state.aiProviders} onChange={this.setAiProvider} isClearable={false} getOptionLabel={option => option.providerName}
              getOptionValue={option => option.providerName}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label sm='1' for='credentialName'>Credentials</Label>
          <Col sm='2'>
            <Select id="credentialName" value={this.state.storedCredentials.find((element) => element.credentialName === this.state.configuration.llmProviderParameters.credential_name)} 
              options={this.state.storedCredentials} onChange={this.setIndexDistanceMetric} isClearable={false} getOptionLabel={option => option.credentialName}
              getOptionValue={option => option.credentialName}/>
          </Col>
        </FormGroup>
        <FormGroup row>
          <Label for="additionParameters" sm="1">Additional Parms</Label>
          <Col sm="3">
            <Input type="text" id="additionalParameters" title='Enter any additional parameters in JSON format' placeholder='Enter any additional parameters in JSON format' 
              value={this.state.additionalParameters} onChange={this.setAdditionalParameters}
            />
          </Col>
        </FormGroup>        
        <FormGroup row>
          <Col sm='1'>
            <Button type="submit" color="primary" disabled={this.state.saveButtonDisabled} onClick={this.saveChanges}>Save</Button>
          </Col>
        </FormGroup>
      </div>
    );
  }  

  async saveChanges()
  {
    var additionalParametersJ;
    try
    {
      additionalParametersJ = JSON.parse(this.state.additionalParameters);
    }
    catch (e)
    {
      this.context.postNotification('error', 'Additional Parameters must be a valid JSON value.'); 
      return;
    }

    let body = {};
    body.configuration = this.state.configuration;
    body.configuration.llmProviderParameters.additionalParameters = additionalParametersJ;

    let result = await this.context.dbTwig.callRestAPI('dgBunker', 'updateRagConfig', body);
    if ('success' !== result.status) return this.context.dbTwig.apiErrorHandler(result, 'Unable to find similar images');
    this.context.postNotification('success', 'RAG Server configuration updated.');
  }

  setAdditionalParameters(event)
  {
    this.setState({additionalParameters: event.target.value, saveButtonDisabled: false});
  }

  setAiProvider(selectedValue, actionMetaD)
  {
    let configuration = this.state.configuration;
    configuration.llmProviderParameters.provider = selectedValue.label;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setConfigurationValue(key, valueAsNumber)
  {
    let configuration = this.state.configuration;
    configuration[key] = valueAsNumber;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setCredentialName(selectedValue, actionMetaD)
  {
    let configuration = this.state.configuration;
    configuration.llmProviderParameters.credential_name = selectedValue.label;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setIndexDistanceMetric(selectedValue, actionMetaD)
  {
    let configuration = this.state.configuration;
    configuration.indexParameters.distance = selectedValue.label;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setIndexParameterValue(key, valueAsNumber)
  {
    let configuration = this.state.configuration;
    configuration.indexParameters[key] = valueAsNumber;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setIndexType(selectedValue, actionMetaD)
  {
    let configuration = this.state.configuration;
    configuration.indexParameters.indexType = selectedValue.value;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setNumericChunkingParameter(key, valueAsNumber)
  {
    let configuration = this.state.configuration;
    configuration.chunkingParameters[key] = valueAsNumber;
    this.setState({configuration, saveButtonDisabled: false});
  }

  setChunkingParameter(key, selectedValue, actionMetaD)
  {
    let configuration = this.state.configuration;
    configuration.chunkingParameters[key] = selectedValue.value;
    this.setState({configuration, saveButtonDisabled: false});
  }

  toggleNormOption(key)
  {
    let configuration = this.state.configuration;
    let optionFound = configuration.chunkingParameters.norm_options.find((option) =>  option ===  key);
    if (undefined !== optionFound)
    {
      const index = configuration.chunkingParameters.norm_options.indexOf(key);
      configuration.chunkingParameters.norm_options.splice(index, 1);
    }
    else
      configuration.chunkingParameters.norm_options.push(key);

    this.setState({configuration, saveButtonDisabled: false});
  }
}

RagServerConfig.contextType = AppContext;

export default RagServerConfig
